import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { toast } from "sonner";
import { MessageSquareHeart } from "lucide-react";

const DISMISS_KEY = "claro_trial_survey_dismissed";
const HIDE_KEY = "claro_trial_survey_hidden";  // persistent "don't show again"

const SATISFACTION = ["Very Satisfied", "Satisfied", "Neutral", "Dissatisfied", "Very Dissatisfied"];
const ONBOARDING = ["Very Easy", "Easy", "Neutral", "Difficult", "Very Difficult"];
const VALUE_FEATURES = [
  "Standards Library & CFR Matrix mapping",
  "Inherent Risk Assessment (IRA) & Readiness Gaps tracking",
  "STaR (Standard Transition & Reconciliation crosswalks)",
  "ClaroLens (structured self-assessments)",
  "Program Authoring: Documents (5 P's), Policy Manuals, SOPs & Forms",
  "CIP-013 SCRM (Supply Chain Vendor Risk)",
  "AI Tools: Document Checker & Data Analyzer",
  "Platform conveniences (Hover-to-define glossary terms, collapsible panels)",
];
const ROADBLOCKS = [
  "Trial export/download restrictions",
  "Upload limits (3 files / 20 MB cap)",
  "AI credit limits (50 starting credits)",
  "Learning curve / navigation between modules",
  "Missing enterprise modules (e.g., ClaroFlow ticketing, RSAW workspace, SDP)",
  "Other (please specify)",
];
const TIERS = [
  "ClaroShield Light — Unlocks full program-authoring tools without trial caps, includes document export/download capabilities, and expanded AI allotments.",
  "ClaroShield (Enterprise) — Our complete governance platform featuring confidential asset & evidence storage, automated RSAW workspaces, ClaroFlow ticketing, and the Standards Development Process (SDP) module.",
  "Neither at this time",
];

const Section = ({ n, title, children }) => (
  <div className="space-y-4 border-t border-slate-200 pt-5 first:border-t-0 first:pt-0">
    <div className="text-[11px] font-semibold uppercase tracking-widest text-primary">Section {n}</div>
    <div className="text-sm font-semibold text-slate-900">{title}</div>
    {children}
  </div>
);

const RadioRow = ({ testid, options, value, onChange }) => (
  <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-3" data-testid={testid}>
    {options.map((o) => (
      <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
        <RadioGroupItem value={o} data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`} />
        <span>{o}</span>
      </label>
    ))}
  </RadioGroup>
);

const CheckList = ({ testid, options, values, toggle }) => (
  <div className="space-y-2" data-testid={testid}>
    {options.map((o) => (
      <label key={o} className="flex items-start gap-2 text-sm cursor-pointer">
        <Checkbox
          checked={values.includes(o)}
          onCheckedChange={() => toggle(o)}
          className="mt-0.5"
          data-testid={`${testid}-opt`}
        />
        <span className="leading-snug">{o}</span>
      </label>
    ))}
  </div>
);

export const TrialSurveyModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [expired, setExpired] = useState(false);
  const [hidden, setHidden] = useState(false);

  const [r, setR] = useState({
    satisfaction: "", onboarding: "",
    value_features: [], liked_most: "",
    roadblocks: [], roadblock_other: "", improvements: "",
  });
  const [wantsContact, setWantsContact] = useState("");
  const [tiers, setTiers] = useState([]);
  const [contact, setContact] = useState({ name: "", company: "", work_email: "", phone: "", preferred_time: "" });

  const toggle = (key, o) =>
    setR((p) => ({ ...p, [key]: p[key].includes(o) ? p[key].filter((x) => x !== o) : [...p[key], o] }));
  const toggleTier = (o) =>
    setTiers((p) => (p.includes(o) ? p.filter((x) => x !== o) : [...p, o]));

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/trial/survey/me", { __silent402: true });
      setAlreadyDone(!!data?.submitted);
      setExpired(!!data?.expired);
      if (data?.prefill) {
        setContact((c) => ({
          ...c,
          name: c.name || data.prefill.name || "",
          company: c.company || data.prefill.company || "",
          work_email: c.work_email || data.prefill.work_email || "",
          phone: c.phone || data.prefill.phone || "",
        }));
      }
      const dismissed = sessionStorage.getItem(DISMISS_KEY) === "1";
      const hide = localStorage.getItem(HIDE_KEY) === "1";
      setHidden(hide);
      // Re-openable: show on each login while the trial is expired, regardless of
      // whether feedback was already submitted — unless the user chose "Don't show
      // again" (persistent) or dismissed it this session.
      if (data?.expired && !hide && !dismissed) setOpen(true);
    } catch (e) { /* no active tenant / not expired */ }
  }, [user]);

  useEffect(() => { check(); }, [check]);

  useEffect(() => {
    const reopen = () => { setOpen(true); };
    window.addEventListener("claro:open-trial-survey", reopen);
    return () => window.removeEventListener("claro:open-trial-survey", reopen);
  }, []);

  // Keep the global paywall/upgrade modal from stacking on top of (and blocking)
  // the survey: expired trials fire background 402s that would otherwise pop it.
  useEffect(() => {
    if (open) {
      window.__claroTrialSurveyOpen = true;
      window.dispatchEvent(new CustomEvent("claro:trial-survey-open"));
    } else {
      window.__claroTrialSurveyOpen = false;
    }
  }, [open]);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setOpen(false);
  };

  const dontShowAgain = () => {
    try { localStorage.setItem(HIDE_KEY, "1"); } catch { /* noop */ }
    setHidden(true);
    setOpen(false);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/trial/survey", {
        responses: r,
        contact,
        wants_contact: wantsContact || null,
        interested_tiers: tiers,
      });
      setAlreadyDone(true);
      try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
      setOpen(false);
      toast.success("Thank you! Your feedback has been sent to Claro Compliance, LLC.");
    } catch (e) {
      toast.error("Could not submit your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {expired && !hidden && !open && (
        <button
          data-testid="reopen-trial-survey"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-40 flex items-center gap-2 bg-primary text-white shadow-lg hover:bg-primary/90 px-3 py-2 text-sm rounded-none"
          title="Share feedback about your ClaroShield Light trial"
        >
          <MessageSquareHeart className="h-4 w-4" /> Trial feedback
        </button>
      )}
      <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="trial-survey-modal" className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MessageSquareHeart className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle data-testid="trial-survey-title" className="text-lg">
                ClaroShield Light Free Trial Feedback
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-600">
                Thank you for trying ClaroShield Light. Please take 2–3 minutes to share your feedback so we can keep improving.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
          <div className="space-y-6 py-2">
            <Section n="1" title="Overall Experience">
              <div className="space-y-2">
                <Label className="text-sm">How satisfied were you with your ClaroShield Light trial experience?</Label>
                <RadioRow testid="survey-satisfaction" options={SATISFACTION} value={r.satisfaction}
                  onChange={(v) => setR((p) => ({ ...p, satisfaction: v }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">How intuitive was the onboarding and initial setup (MFA setup, entity confirmation, 15-Minute Quick Start)?</Label>
                <RadioRow testid="survey-onboarding" options={ONBOARDING} value={r.onboarding}
                  onChange={(v) => setR((p) => ({ ...p, onboarding: v }))} />
              </div>
            </Section>

            <Section n="2" title="What You Liked">
              <div className="space-y-2">
                <Label className="text-sm">Which tools or features provided the most value? (Select all that apply)</Label>
                <CheckList testid="survey-value-features" options={VALUE_FEATURES}
                  values={r.value_features} toggle={(o) => toggle("value_features", o)} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">What did you like most about using ClaroShield Light?</Label>
                <Textarea data-testid="survey-liked-most" value={r.liked_most}
                  onChange={(e) => setR((p) => ({ ...p, liked_most: e.target.value }))}
                  placeholder="Your thoughts…" rows={3} />
              </div>
            </Section>

            <Section n="3" title="What You Did Not Like / Areas for Improvement">
              <div className="space-y-2">
                <Label className="text-sm">Did you encounter any roadblocks or frustrations? (Select all that apply)</Label>
                <CheckList testid="survey-roadblocks" options={ROADBLOCKS}
                  values={r.roadblocks} toggle={(o) => toggle("roadblocks", o)} />
                {r.roadblocks.includes("Other (please specify)") && (
                  <Input data-testid="survey-roadblock-other" value={r.roadblock_other}
                    onChange={(e) => setR((p) => ({ ...p, roadblock_other: e.target.value }))}
                    placeholder="Please specify" className="mt-1" />
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm">What would you need to see before adopting ClaroShield into your daily operations?</Label>
                <Textarea data-testid="survey-improvements" value={r.improvements}
                  onChange={(e) => setR((p) => ({ ...p, improvements: e.target.value }))}
                  placeholder="Features, workflows, or adjustments…" rows={3} />
              </div>
            </Section>

            <Section n="4" title="Next Steps & Future Solutions">
              <div className="space-y-2">
                <Label className="text-sm">Would you like a representative from Claro Compliance, LLC to contact you?</Label>
                <RadioRow testid="survey-wants-contact"
                  options={["Yes, please reach out via email", "Yes, please reach out via phone", "Not at this time"]}
                  value={wantsContact} onChange={setWantsContact} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Are you interested in exploring our other product tiers? (Select all that apply)</Label>
                <CheckList testid="survey-tiers" options={TIERS} values={tiers} toggle={toggleTier} />
              </div>
            </Section>

            <Section n="5" title="Contact Information (optional / pre-filled)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input data-testid="survey-contact-name" value={contact.name}
                    onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Company / Registered Entity</Label>
                  <Input data-testid="survey-contact-company" value={contact.company}
                    onChange={(e) => setContact((c) => ({ ...c, company: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Work Email</Label>
                  <Input data-testid="survey-contact-email" type="email" value={contact.work_email}
                    onChange={(e) => setContact((c) => ({ ...c, work_email: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone Number</Label>
                  <Input data-testid="survey-contact-phone" value={contact.phone}
                    onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">Preferred Contact Time</Label>
                  <Input data-testid="survey-contact-time" value={contact.preferred_time}
                    onChange={(e) => setContact((c) => ({ ...c, preferred_time: e.target.value }))}
                    placeholder="e.g., Weekday mornings ET" />
                </div>
              </div>
            </Section>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-200 pt-4 sm:justify-between">
          <Button variant="ghost" data-testid="survey-dont-show" onClick={dontShowAgain} disabled={submitting}
                  className="text-slate-500 hover:text-slate-700">
            Don't show again
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="survey-dismiss" onClick={dismiss} disabled={submitting}>
              Maybe later
            </Button>
            <Button data-testid="survey-submit" onClick={submit} disabled={submitting}>
              {submitting ? "Sending…" : (alreadyDone ? "Send again" : "Submit feedback")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default TrialSurveyModal;
